import {createReducer, on} from "@ngrx/store";
import { LocalDate } from '@js-joda/core';
import * as RestrictionActions from './restriction.actions';
import { initialRestrictionState } from "./restriction.state";
import { TaskDefinition, TaskDefinitionStatus, TaskExecutionStatus } from '@core/models/domain/restriction.domain';
import { ModifyTaskDefinitionRequest, TaskDefinitionModifyType } from '@core/models/request/restriction.request';
import { convertDateToLocalDate, convertZonedDateTimeToDate } from '@core/utils/utils';
import { getRepeatSelectedDays } from '@core/utils/service';

// How long a locally mutated task definition is shielded from being overwritten
// by a definitions reload. The backend read model (CQRS projection) can lag
// behind an accepted command; one auto-reload cycle is enough for it to catch up.
export const RECENT_WRITE_PROTECTION_MS = 10000;

// A FUTURE_OCCURENCE modify of a recurring series whose start DATE moved is a
// deactivate+recreate on the backend: the old series keeps running until the day before
// the new start, and the replacement definition arrives with the success echo.
function isRecreateModify(def: TaskDefinition, request: ModifyTaskDefinitionRequest): boolean {
  const isRecurringSeries = (def.repeatSelectedDays?.length ?? 0) > 0;
  if (!isRecurringSeries || !request.startAt || !def.startAt) {
    return false;
  }
  return !convertDateToLocalDate(def.startAt).equals(request.startAt.toLocalDate());
}

// Mirrors the backend deactivation of the replaced series: validity shortened to the day
// before the new start; INACTIVE only when that cutoff is already past (same rules as the
// cancel-from-date optimistic update).
function applyRecreateCutoff(def: TaskDefinition, request: ModifyTaskDefinitionRequest): TaskDefinition {
  const cutoff = request.startAt.toLocalDate().minusDays(1);
  const cutoffDate = new Date(cutoff.year(), cutoff.monthValue() - 1, cutoff.dayOfMonth());
  const status = cutoff.isBefore(LocalDate.now())
    ? TaskDefinitionStatus.INACTIVE
    : TaskDefinitionStatus.ACTIVE;
  return { ...def, status, validityEndDate: cutoffDate };
}

// Mirrors the fields an in-place modify changes on the backend, so the optimistic
// update matches what a fresh reload would eventually return.
function applyModifyRequest(def: TaskDefinition, request: ModifyTaskDefinitionRequest): TaskDefinition {
  if (request.whatToModify === TaskDefinitionModifyType.THIS_OCCURENCE) {
    // One-occurrence edit: the backend leaves the series intact and only cancels the
    // occurrence's date (the detached one-time replacement arrives with the success echo).
    // The request is shaped as a one-time, so patching the series from it would hide every
    // other occurrence until the shield expires.
    if (!request.deactivationDate) {
      return def;
    }
    const d = request.deactivationDate.toLocalDate();
    const cancelledDate = new Date(d.year(), d.monthValue() - 1, d.dayOfMonth());
    return { ...def, cancelledTaskDates: [...(def.cancelledTaskDates ?? []), cancelledDate] };
  }
  if (isRecreateModify(def, request)) {
    return applyRecreateCutoff(def, request);
  }
  return {
    ...def,
    title: request.title,
    description: request.description,
    repeatSelectedDays: getRepeatSelectedDays((request.repeatSelectedDays ?? []).map(String)),
    startAt: request.startAt ? convertZonedDateTimeToDate(request.startAt) : def.startAt,
    endAt: request.endAt ? convertZonedDateTimeToDate(request.endAt) : def.endAt,
    schoolInfo: request.taskSchoolInfo ?? def.schoolInfo,
  };
}

export const restrictionReducer = createReducer(
  initialRestrictionState,

  // LOAD TASK DEFINITIONS
  on(RestrictionActions.loadTaskDefinitions, (state) => ({
    ...state,
    taskDefinitionLoading: true,
    taskDefinitionError: null,
  })),
  on(RestrictionActions.loadTaskDefinitionsSuccess, (state, { taskDefinitions }) => {
    // Definitions mutated locally in the last few seconds win over the incoming
    // list: the read model may not reflect the mutation yet, and blindly taking
    // the server list would make just-created/edited/cancelled restrictions
    // flicker back to their old state until the next auto-reload.
    const now = Date.now();
    const recentLocalWrites: Record<string, number> = {};
    Object.entries(state.recentLocalWrites).forEach(([id, at]) => {
      if (now - at < RECENT_WRITE_PROTECTION_MS) {
        recentLocalWrites[id] = at;
      }
    });

    const protectedIds = new Set(Object.keys(recentLocalWrites));
    const localById = new Map(state.taskDefinitions.map((def) => [def.id, def]));
    const merged = taskDefinitions.map((incoming) =>
      incoming.id && protectedIds.has(incoming.id) ? localById.get(incoming.id)! : incoming
    );
    const incomingIds = new Set(taskDefinitions.map((def) => def.id));
    protectedIds.forEach((id) => {
      if (!incomingIds.has(id) && localById.has(id)) {
        merged.push(localById.get(id)!);
      }
    });

    return {
      ...state,
      taskDefinitionLoading: false,
      taskDefinitionsLoaded: true,
      taskDefinitions: merged,
      recentLocalWrites,
    };
  }),
  on(RestrictionActions.loadTaskDefinitionsFailure, (state, { error }) => ({
    ...state,
    taskDefinitionLoading: false,
    taskDefinitionsLoaded: true,
    taskDefinitionError: error,
  })),

  // CREATE TASK DEFINITION
  on(RestrictionActions.createTaskDefinition, (state) => ({
    ...state,
    taskDefinitionLoading: true,
  })),
  on(RestrictionActions.createTaskDefinitionSuccess, (state, { taskDefinition }) => ({
    ...state,
    taskDefinitionLoading: false,
    taskDefinitionError: undefined,
    taskDefinitions: [...state.taskDefinitions, taskDefinition],
    recentLocalWrites: taskDefinition.id
      ? { ...state.recentLocalWrites, [taskDefinition.id]: Date.now() }
      : state.recentLocalWrites,
  })),
  on(RestrictionActions.createTaskDefinitionFailure, (state, { error }) => ({
    ...state,
    taskDefinitionLoading: false,
    taskDefinitionError: error,
  })),

  // UPDATE TASK DEFINITION
  on(RestrictionActions.updateTaskDefinition, (state, { request }) => ({
    // Optimistically apply the edit so the list reflects it instantly. The modify
    // response body can echo pre-modification data while the backend read model
    // lags, so the request — not the echo — is the source of truth for what changed.
    ...state,
    taskDefinitionLoading: true,
    taskDefinitions: state.taskDefinitions.map((def) =>
      def.id === request.taskDefinitionId ? applyModifyRequest(def, request) : def
    ),
    recentLocalWrites: {
      ...state.recentLocalWrites,
      [request.taskDefinitionId]: Date.now(),
    },
  })),
  on(RestrictionActions.updateTaskDefinitionSuccess, (state, { taskDefinition, request }) => {
    // In-place modify (echoed id already in the store): keep the optimistic patch —
    // the echo may still carry stale read-model data. Deactivate+recreate (new id):
    // the old definition keeps running until the day before the new start; mirror that
    // and append the new one. One-occurrence modify (also a new id — the detached
    // one-time replacement): the series is untouched, just append the replacement.
    const exists = state.taskDefinitions.some((g) => g.id === taskDefinition.id);
    const isThisOccurrence = request?.whatToModify === TaskDefinitionModifyType.THIS_OCCURENCE;
    const taskDefinitions = exists
      ? state.taskDefinitions
      : state.taskDefinitions
          .map((def) => {
            if (def.id !== request.taskDefinitionId || isThisOccurrence) {
              return def;
            }
            return request.startAt
              ? applyRecreateCutoff(def, request)
              : { ...def, status: TaskDefinitionStatus.INACTIVE };
          })
          .concat(taskDefinition);
    return {
      ...state,
      taskDefinitionLoading: false,
      taskDefinitions,
      recentLocalWrites: taskDefinition.id
        ? { ...state.recentLocalWrites, [taskDefinition.id]: Date.now() }
        : state.recentLocalWrites,
    };
  }),
  on(RestrictionActions.updateTaskDefinitionFailure, (state, { error, request }) => {
    // Stop shielding the optimistically patched definition so the reload triggered
    // by the failure effect restores the server state.
    const recentLocalWrites = { ...state.recentLocalWrites };
    if (request) {
      delete recentLocalWrites[request.taskDefinitionId];
    }
    return {
      ...state,
      taskDefinitionLoading: false,
      taskDefinitionError: error,
      recentLocalWrites,
    };
  }),

  // CANCEL TASK
  on(RestrictionActions.cancelTask, (state, { request, date }) => {
    console.log('🗑️ Reducer: Optimistically cancelling task:', request);

    // Optimistic update - update execution status to RESTRICTION_CANCELLED
    const updatedExecutions = { ...state.taskExecutions };
    if (request.taskExecutionId && updatedExecutions[date]) {
      const executions = updatedExecutions[date];
      updatedExecutions[date] = executions.map(exec =>
        exec.id === request.taskExecutionId
          ? { ...exec, status: TaskExecutionStatus.RESTRICTION_CANCELLED }
          : exec
      );
    }

    // Optimistically mirror on the definition exactly what the backend call will do,
    // and nothing more, so the UI never shows unrelated occurrences as removed:
    // - execution cancel: the definition is untouched (only the execution changes);
    // - cancel a single (not yet executed) date: add it to cancelledTaskDates;
    // - deactivate "from a date": shorten the validity window — the definition stays
    //   ACTIVE (validityEndDate = day before the from-date) while the cutoff is today
    //   or later, so earlier occurrences keep showing. A full "cancel all" (cutoff
    //   already in the past) marks it INACTIVE.
    const updatedDefinitions = state.taskDefinitions.map(def => {
      if (def.id !== request.taskDefinitionId) {
        return def;
      }
      if (request.deactivate && request.deactivateFromDate) {
        const cutoff = request.deactivateFromDate.minusDays(1);
        const deactivationCutoff = new Date(cutoff.year(), cutoff.monthValue() - 1, cutoff.dayOfMonth());
        const optimisticStatus = cutoff.isBefore(LocalDate.now())
          ? TaskDefinitionStatus.INACTIVE
          : TaskDefinitionStatus.ACTIVE;
        return { ...def, status: optimisticStatus, validityEndDate: deactivationCutoff };
      }
      if (!request.taskExecutionId && request.dateToCancel) {
        const d = request.dateToCancel;
        const cancelledDate = new Date(d.year(), d.monthValue() - 1, d.dayOfMonth());
        return { ...def, cancelledTaskDates: [...(def.cancelledTaskDates ?? []), cancelledDate] };
      }
      return def;
    });

    return {
      ...state,
      taskDefinitionLoading: true,
      taskDefinitions: updatedDefinitions,
      taskExecutions: updatedExecutions,
      recentLocalWrites: {
        ...state.recentLocalWrites,
        [request.taskDefinitionId]: Date.now(),
      },
    };
  }),
  on(RestrictionActions.cancelTaskSuccess, (state, { id, date })=> {
    console.log('✅ Reducer: Task successfully cancelled on server:', id);
    // Task already updated optimistically, just update loading state
    return {
      ...state,
      taskDefinitionLoading: false
    };
  }),
  on(RestrictionActions.cancelTaskFailure, (state, { error, request, date }) => {
    // Roll back the optimistic execution update and stop shielding the definition,
    // so the reload triggered by the failure effect restores the server state.
    const updatedExecutions = { ...state.taskExecutions };
    if (request.taskExecutionId && request.taskExecutionStatus && updatedExecutions[date]) {
      updatedExecutions[date] = updatedExecutions[date].map(exec =>
        exec.id === request.taskExecutionId
          ? { ...exec, status: request.taskExecutionStatus as TaskExecutionStatus }
          : exec
      );
    }
    const recentLocalWrites = { ...state.recentLocalWrites };
    delete recentLocalWrites[request.taskDefinitionId];

    return {
      ...state,
      taskDefinitionLoading: false,
      taskDefinitionError: error,
      taskExecutions: updatedExecutions,
      recentLocalWrites,
    };
  }),

  // LOAD TASK EXECUTIONS
  on(RestrictionActions.loadTaskExecutions, (state) => ({
    ...state,
    taskExecutionLoading: true,
    taskExecutionError: null,
  })),
  on(
    RestrictionActions.loadTaskExecutionsSuccess,
    (state, { date, taskExecutions }) => {
      console.log(`🟦 Reducer: Storing task executions for date ${date}:`, taskExecutions);
      console.log(`🟦 Reducer: Current state taskExecutions:`, state.taskExecutions);
      
      // Preserve locally cancelled executions - don't let auto-reload overwrite them
      const existingExecutions = state.taskExecutions[date] || [];
      const cancelledExecutionIds = existingExecutions
        .filter(exec => exec.status === TaskExecutionStatus.RESTRICTION_CANCELLED)
        .map(exec => exec.id);
      
      console.log(`🟦 Reducer: Found ${cancelledExecutionIds.length} locally cancelled executions to preserve`);
      
      // Merge: Keep cancelled executions from state, add new ones from backend (unless they're cancelled)
      const mergedExecutions = taskExecutions.map(backendExec => {
        if (cancelledExecutionIds.includes(backendExec.id)) {
          // This execution was cancelled locally - keep the local RESTRICTION_CANCELLED status
          const localExec = existingExecutions.find(e => e.id === backendExec.id);
          console.log(`🟦 Preserving locally cancelled execution ${backendExec.id}`);
          return localExec!;
        }
        return backendExec;
      });
      
      return {
        ...state,
        taskExecutionLoading: false,
        taskExecutions: {
          ...state.taskExecutions,
          [date]: mergedExecutions,
        },
      };
    }
  ),
  on(RestrictionActions.loadTaskExecutionsFailure, (state, { error }) => {
    console.error(`🔴 Reducer: Failed to load task executions:`, error);
    return {
      ...state,
      taskExecutionLoading: false,
      taskExecutionError: error,
    };
  }),

  // LOAD PARENT CANCELLATIONS
  on(RestrictionActions.loadParentCancellations, (state) => ({
    ...state,
    parentCancellationsLoading: true,
    parentCancellationsError: null,
  })),
  on(RestrictionActions.loadParentCancellationsSuccess, (state, { cancellations }) => ({
    ...state,
    parentCancellationsLoading: false,
    parentCancellations: cancellations,
  })),
  on(RestrictionActions.loadParentCancellationsFailure, (state, { error }) => ({
    ...state,
    parentCancellationsLoading: false,
    parentCancellationsError: error,
  })),

  // LOAD SCHOOL PARENT CANCELLATIONS
  on(RestrictionActions.loadSchoolParentCancellations, (state) => ({
    ...state,
    schoolParentCancellationsLoading: true,
    schoolParentCancellationsError: null,
  })),
  on(RestrictionActions.loadSchoolParentCancellationsSuccess, (state, { cancellations }) => ({
    ...state,
    schoolParentCancellationsLoading: false,
    schoolParentCancellations: cancellations,
  })),
  on(RestrictionActions.loadSchoolParentCancellationsFailure, (state, { error }) => ({
    ...state,
    schoolParentCancellationsLoading: false,
    schoolParentCancellationsError: error,
  }))
);